import { useStreamingGatewayUrl, useStreamingGatewayAuthToken } from './streamingGatewayApi';
import { LlmConversation } from './llmConversationsService';
import { useTimezoneService } from './timezoneService';

export enum ChatMessageChunkType {
  CONVERSATION_CREATED = 'conversation-created',
  USER_MESSAGE_SAVED = 'user-message-saved',
  TEXT_DELTA = 'text-delta',
  TOOL_CALL = 'tool-call',
  TOOL_RESULT = 'tool-result',
  ASSISTANT_MESSAGE_SAVED = 'assistant-message-saved',
  FINISH = 'finish',
  ERROR = 'error',
  CONTEXT_SUMMARIZED = 'context-summarized',
  TITLE_GENERATED = 'title-generated',
  PENDING_ACTION = 'pending-action',
}

export interface ChatMessageChunk {
  type: ChatMessageChunkType;
  text?: string;
  conversation?: LlmConversation;
  usage?: {
    inputTokens: number;
    outputTokens: number;
  };
  estimatedCostUsd?: number;
  error?: string;
  messageId?: number;
  toolCallId?: string;
  toolName?: string;
  displayMessage?: string | null;
  actionType?: string;
  actionPayload?: Record<string, unknown>;
}

export type OnChunkCallback = (chunk: ChatMessageChunk) => void;
export type AbortStreamFunction = () => void;

async function streamNdjson(
  url: string,
  body: object,
  token: string,
  timezone: string,
  onChunk: OnChunkCallback,
  signal: AbortSignal,
  method: 'POST' | 'PUT' = 'POST',
): Promise<void> {
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-NEKST-TIMEZONE': timezone,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const parsed = JSON.parse(errorText) as { error?: string };
      errorMessage = parsed.error || errorText;
    } catch {
      errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  if (!response.body) {
    throw new Error('Response body is not available');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (!done && result.value) {
        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk = JSON.parse(line) as ChatMessageChunk;
              onChunk(chunk);

              if (chunk.type === ChatMessageChunkType.ERROR) {
                throw new Error(chunk.error || 'Stream error');
              }
            } catch (parseError) {
              if (parseError instanceof SyntaxError) {
                console.error('Failed to parse NDJSON line:', line);
              } else {
                throw parseError;
              }
            }
          }
        }
      }
    }

    if (buffer.trim()) {
      try {
        const chunk = JSON.parse(buffer) as ChatMessageChunk;
        onChunk(chunk);
      } catch {
        console.error('Failed to parse remaining buffer:', buffer);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export type EntityContextType = 'transaction' | 'task' | 'contact' | 'team_member';

export interface EntityContext {
  type: EntityContextType;
  id: number;
  name?: string;
}

export interface StartConversationRequest {
  message: string;
  model?: string;
  context?: EntityContext[];
  enableEmailDescriptionTool?: boolean;
}

export interface SendMessageRequest {
  message: string;
  context?: EntityContext[];
  enableEmailDescriptionTool?: boolean;
}

export interface EditMessageRequest {
  message: string;
  context?: EntityContext[];
  enableEmailDescriptionTool?: boolean;
}

export function useLlmChatService() {
  const streamingGatewayUrl = useStreamingGatewayUrl();
  const authToken = useStreamingGatewayAuthToken();
  const timezoneService = useTimezoneService();

  const startConversation = (
    request: StartConversationRequest,
    onChunk: OnChunkCallback,
  ): AbortStreamFunction => {
    const controller = new AbortController();

    streamNdjson(
      `${streamingGatewayUrl}/chats`,
      request,
      authToken,
      timezoneService.getTimezone(),
      onChunk,
      controller.signal,
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        onChunk({
          type: ChatMessageChunkType.ERROR,
          error: error.message || 'Unknown error occurred',
        });
      }
    });

    return () => controller.abort();
  };

  const sendMessage = (
    conversationId: number,
    request: SendMessageRequest,
    onChunk: OnChunkCallback,
  ): AbortStreamFunction => {
    const controller = new AbortController();

    streamNdjson(
      `${streamingGatewayUrl}/chats/${conversationId}/message`,
      request,
      authToken,
      timezoneService.getTimezone(),
      onChunk,
      controller.signal,
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        onChunk({
          type: ChatMessageChunkType.ERROR,
          error: error.message || 'Unknown error occurred',
        });
      }
    });

    return () => controller.abort();
  };

  const editMessage = (
    conversationId: number,
    messageId: number,
    request: EditMessageRequest,
    onChunk: OnChunkCallback,
  ): AbortStreamFunction => {
    const controller = new AbortController();

    streamNdjson(
      `${streamingGatewayUrl}/chats/${conversationId}/messages/${messageId}`,
      request,
      authToken,
      timezoneService.getTimezone(),
      onChunk,
      controller.signal,
      'PUT',
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        onChunk({
          type: ChatMessageChunkType.ERROR,
          error: error.message || 'Unknown error occurred',
        });
      }
    });

    return () => controller.abort();
  };

  return {
    startConversation,
    sendMessage,
    editMessage,
  };
}
